import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Button,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Spinner,
} from "reactstrap";
import { FaBolt, FaLayerGroup, FaInfinity } from "react-icons/fa";
import requests from "../requests";
import { usePostHog } from "@posthog/react";
import { useSeo } from "../hooks/useSeo";
import "../style/pricing-styles.css";

// Keep these three in sync with COMPARISON_ROWS below — they are the same claim in
// headline form, and the table is what a reader checks them against.
const DIFFERENTIATORS = [
  {
    icon: FaInfinity,
    iconClass: "differentiator-icon-unlimited",
    title: "Unlimited Query Matcher",
    body: "Free runs 20 analog searches a day. Pro runs as many as you want — every search is a fresh similarity run across 20 years of history.",
  },
  {
    icon: FaBolt,
    iconClass: "differentiator-icon-realtime",
    title: "Alerts that do the watching",
    body: "Describe the setups you care about — an analog signal, a chart pattern, an indicator reading or a momentum rank, optionally only on your watchlist — and get an email when a ticker matches. Your feed collects every match in one place.",
  },
  {
    icon: FaLayerGroup,
    iconClass: "differentiator-icon-windows",
    title: "The daily digest",
    body: "The day's top bullish and bearish signals in your inbox every trading day, without opening the site. Free accounts get the same list as a Saturday recap.",
  },
];

// The paid axis is COMPUTE + CONVENIENCE, not timeliness or window depth.
// Until 2026-08-10 Pro sold "same day" data and the 6m/1y windows. The 2026-07-31 audit
// found no directional edge clearing the matched null, which makes charging for *earlier
// and deeper* access to a directional call indefensible — you are selling a head start on
// a number whose direction has no demonstrated value. Everything the engine produces is now
// free to everyone, at the same time. Pro sells what genuinely costs us money or saves the
// user real work: unlimited DTW queries (CPU), alerts and the feed (infrastructure), the
// digest, exports. Those are honest to charge for regardless of whether an edge exists.
const FREE_FEATURES = [
  { text: "Today's featured matches — same data, same time as Pro", included: true },
  { text: "Every time window: 2-week through 1-year", included: true },
  { text: "Classic chart patterns — today, all windows", included: true },
  { text: "Full Leaders & Laggards rankings", included: true },
  { text: "Query Matcher (20 searches/day)", included: true },
  { text: "Watchlist tracking", included: true },
  { text: "Weekly email recap (Saturdays)", included: true },
  { text: "Unlimited Query Matcher", included: false },
  { text: "Custom alerts & personalized feed", included: false },
];

// `planned` items render without a check mark: they don't exist yet.
const PRO_FEATURES = [
  { text: "Unlimited Query Matcher — run as many analog searches as you like" },
  { text: "Custom alerts on signals, patterns, indicators and momentum, by email" },
  { text: "Personalized feed of everything your alerts match" },
  { text: "Daily email digest — the day's top signals, every trading day" },
  { text: "Everything in Free, at the same time as everyone else" },
  { text: "Priority support" },
  { text: "Data export & API access (planned)", planned: true },
];

const COMPARISON_ROWS = [
  { feature: "Data freshness", free: "Same day", pro: "Same day" },
  { feature: "Featured match list", free: "Full", pro: "Full" },
  { feature: "Featured windows", free: "All (2w – 1yr)", pro: "All (2w – 1yr)" },
  { feature: "Classic pattern windows", free: "All (1m – 1yr)", pro: "All (1m – 1yr)" },
  { feature: "Leaders & Laggards", free: "Full", pro: "Full" },
  { feature: "Query Matcher", free: "20/day", pro: "✓ Unlimited" },
  { feature: "Watchlist", free: "✓", pro: "✓" },
  { feature: "Custom alerts (email)", free: "—", pro: "✓" },
  { feature: "Personalized feed", free: "—", pro: "✓" },
  { feature: "Email digest", free: "Weekly (Sat)", pro: "✓ Every trading day" },
  { feature: "Data export / API", free: "—", pro: "Planned" },
];

const FAQ_ITEMS = [
  {
    id: "founding",
    q: "What's the founding offer?",
    a: "The first 1,000 members get Pro at a founding rate of $10/month or $100/year (regularly $29/month). As long as you stay subscribed, you keep that rate — it won't go up on you. Once the 1,000 founding spots are filled, new members pay the regular price.",
  },
  {
    id: "1",
    q: "Can I try Pro before paying?",
    a: "Yes — your first Pro subscription starts with a 14-day free trial with full access to every feature. You add a payment method up front, but you're not charged until the trial ends, and you can cancel anytime before then to avoid any charge.",
  },
  {
    id: "2",
    q: "What's included in the free plan?",
    a: "Everything the engine produces: today's featured matches and classic chart patterns, every time window from 2 weeks to 1 year, the full Leaders & Laggards rankings, 20 Query Matcher searches a day, and watchlist tracking. Free accounts also get a weekly email recap on Saturdays. Free and Pro see the same data at the same time — we don't hold anything back or delay it. Pro adds unlimited searches, custom alerts, the personalized feed, and the digest every trading day.",
  },
  {
    id: "3",
    q: "Can I cancel at any time?",
    a: "Absolutely. You can cancel your subscription at any time from your account settings. You'll retain Pro access through the end of your current billing period.",
  },
  {
    id: "4",
    q: "Do you offer team or institutional plans?",
    a: "We're working on B2B and API licensing options for hedge funds, family offices, and fintech platforms. Reach out to us at contact@dejachart.com to discuss your needs.",
  },
  {
    id: "5",
    q: "Is this investment advice?",
    a: "No. DejaChart finds historical charts whose shape resembles a current one and shows you what happened after each of them. It's a research tool, not advice, and it doesn't tell you what to buy or sell. All trading involves risk. See the full disclaimer on the About page.",
  },
  {
    id: "6",
    q: "Do you publish an overall accuracy number?",
    a: "No. Every signal is published with the analogs behind it and the full distribution of what followed them, so you can judge each one on its own evidence. A single blended percentage would hide more than it shows.",
  },
];

export default function Pricing() {
  useSeo({
    title: "Pricing — DejaChart Free & Pro Plans",
    description: "Every signal, every window, same day — free. Pro adds unlimited Query Matcher searches, custom alerts, the personalized feed, and the daily digest. 14-day free trial.",
    path: "/pricing",
  });
  const [openAccordion, setOpenAccordion] = React.useState("");
  const [annualBilling, setAnnualBilling] = React.useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const posthog = usePostHog();

  const loggedIn = useSelector((state: RootReducerState) => state.authentication.loggedIn);
  const user = useSelector((state: RootReducerState) => state.authentication.user);
  const isPro = user?.groups?.includes("pro") ?? false;

  useEffect(() => {
    posthog.capture('pricing_page_viewed', { is_logged_in: !!loggedIn, is_pro: isPro });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? "" : id);
  };

  // Founding offer (first 1000 members): $10/mo or $100/yr. To END the promo, set
  // FOUNDING_PROMO = false here AND swap the Stripe price IDs in the dejachart/api-prod
  // secret back to the regular ones (this flag only controls the DISPLAY; the actual
  // amount charged is the Stripe price the checkout uses). See CLAUDE.md billing notes.
  const FOUNDING_PROMO = true as boolean; // founding-offer toggle (widened so it reads as a flag, not a constant)
  const REGULAR_MONTHLY = 29;                                   // post-promo monthly
  const REGULAR_ANNUAL_MONTHLY = Math.round(REGULAR_MONTHLY * 0.8); // post-promo annual ($/mo) = 23
  const monthlyPrice = FOUNDING_PROMO ? 10 : REGULAR_MONTHLY;
  const annualTotal = FOUNDING_PROMO ? 100 : REGULAR_ANNUAL_MONTHLY * 12; // billed-annually total
  const annualMonthlyPrice = Math.round(annualTotal / 12);     // displayed $/mo for annual
  const annualSavingsPct = Math.round((1 - annualTotal / (monthlyPrice * 12)) * 100);
  const regularDisplay = annualBilling ? REGULAR_ANNUAL_MONTHLY : REGULAR_MONTHLY;

  const handleStartTrial = async () => {
    if (!loggedIn) {
      window.location.href = "/signup";
      return;
    }
    const interval = annualBilling ? "annual" : "monthly";
    posthog.capture("checkout_started", { billing_interval: interval });
    setCheckoutLoading(true);
    setCheckoutError("");
    try {
      const res = await requests.post("/api/billing/checkout", { interval });
      window.location.href = res.data.checkout_url;
    } catch {
      setCheckoutError("Unable to start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="pricing-page">
      {/* Hero */}
      <section className="pricing-hero">
        <Container>
          <h1>Simple, Transparent Pricing</h1>
          <p>
            Start free and upgrade when you're ready. No hidden fees,
            no contracts — cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="billing-toggle">
            <span className={!annualBilling ? "active-billing" : ""}>Monthly</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={annualBilling}
                onChange={() => {
                  const next = !annualBilling;
                  setAnnualBilling(next);
                  posthog.capture("billing_interval_toggled", { interval: next ? "annual" : "monthly" });
                }}
              />
              <span className="toggle-slider" />
            </label>
            <span className={annualBilling ? "active-billing" : ""}>Annual</span>
            <span className="annual-savings-badge">Save {annualSavingsPct}%</span>
          </div>

          {FOUNDING_PROMO && (
            <div
              style={{
                marginTop: "1.25rem", padding: "0.65rem 1.1rem", borderRadius: 10,
                background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)",
                fontSize: "0.95rem", display: "inline-block", maxWidth: 560,
              }}
            >
              🎉 <strong>Founding offer</strong> — the first <strong>1,000 members</strong> lock in{" "}
              <strong>$10/mo</strong> or <strong>$100/yr</strong> (regularly ${REGULAR_MONTHLY}/mo).
              Your rate stays locked as long as you're subscribed. Cancel anytime.
            </div>
          )}
        </Container>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-section">
        <Container>
          <Row className="justify-content-center g-4">
            {/* Free */}
            <Col lg="5" md="6">
              <div className="pricing-card-wrapper">
                <div className="pricing-card">
                  <div className="plan-name">Free</div>
                  <div className="plan-price">
                    <span className="price-currency">$</span>
                    <span className="price-amount">0</span>
                  </div>
                  <div className="price-period">forever</div>
                  <div className="price-annual-note">&nbsp;</div>
                  <p className="plan-tagline">
                    Every signal in every window, the same day as Pro.
                    No credit card needed.
                  </p>

                  <ul className="plan-features">
                    {FREE_FEATURES.map((f, i) => (
                      <li key={i} className={!f.included ? "muted-feature" : ""}>
                        <span className={f.included ? "feature-check" : "feature-x"}>
                          {f.included ? "✓" : "✕"}
                        </span>
                        {f.text}
                      </li>
                    ))}
                  </ul>

                  {!isPro && (
                    <Link to="/signup" className="btn btn-outline-primary plan-cta">
                      Get Started Free
                    </Link>
                  )}
                </div>
              </div>
            </Col>

            {/* Pro */}
            <Col lg="5" md="6">
              <div className="pricing-card-wrapper">
                <div className="pricing-card featured-plan">
                  {FOUNDING_PROMO && <div className="popular-badge">Founding rate</div>}
                  <div className="plan-name">Pro</div>
                  <div className="plan-price">
                    <span className="price-currency">$</span>
                    <span className="price-amount">
                      {annualBilling ? annualMonthlyPrice : monthlyPrice}
                    </span>
                  </div>
                  <div className="price-period">/ month</div>
                  {FOUNDING_PROMO && (
                    <div style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginTop: 2 }}>
                      <s>${regularDisplay}/mo</s> regularly · founding rate
                    </div>
                  )}
                  <div className="price-annual-note">
                    {annualBilling
                      ? `Billed annually — $${annualTotal}/yr`
                      : `Or $${annualMonthlyPrice}/mo billed annually`}
                  </div>
                  <p className="plan-tagline">
                    For daily use: unlimited searches, and alerts that
                    bring matching setups to you.
                  </p>

                  <ul className="plan-features">
                    {PRO_FEATURES.map((f, i) => (
                      <li key={i} className={f.planned ? "muted-feature" : ""}>
                        <span className={f.planned ? "feature-x" : "feature-check"}>
                          {f.planned ? "○" : "✓"}
                        </span>
                        {f.text}
                      </li>
                    ))}
                  </ul>

                  {!isPro && checkoutError && (
                    <p style={{ color: "var(--danger, #ef4444)", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                      {checkoutError}
                    </p>
                  )}
                  {isPro ? (
                    <Link to="/account" className="btn btn-primary plan-cta" style={{ width: "100%" }}>
                      Manage Account
                    </Link>
                  ) : (
                    <Button
                      color="primary"
                      className="plan-cta"
                      onClick={handleStartTrial}
                      disabled={checkoutLoading}
                      style={{ width: "100%" }}
                    >
                      {checkoutLoading ? <Spinner size="sm" /> : "Start 14-Day Free Trial"}
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          {/* Enterprise note */}
          <Row className="mt-5">
            <Col className="text-center">
              <p style={{ color: "var(--text-muted)", fontSize: "0.95rem" }}>
                Need API access or institutional pricing?{" "}
                <a
                  href="mailto:contact@dejachart.com?subject=DejaChart Enterprise"
                  style={{ color: "var(--primary)" }}
                >
                  Contact us
                </a>{" "}
                to discuss B2B plans.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Headline Differentiators */}
      <section className="differentiators-section">
        <Container>
          <Row className="g-4">
            {DIFFERENTIATORS.map((d, i) => {
              const Icon = d.icon;
              return (
                <Col md="4" key={i}>
                  <div className="differentiator-card">
                    <div className={`differentiator-icon ${d.iconClass}`}>
                      <Icon size={22} />
                    </div>
                    <h3>{d.title}</h3>
                    <p>{d.body}</p>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Comparison Table */}
      <section className="comparison-section">
        <Container>
          <h2>Full Feature Comparison</h2>
          <div style={{ overflowX: "auto" }}>
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th className="pro-col">Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row, i) => (
                  <tr key={i}>
                    <td>{row.feature}</td>
                    <td>
                      {row.free === "—" ? (
                        <span className="check-no">—</span>
                      ) : row.free === "✓" ? (
                        <span className="check-yes">✓</span>
                      ) : (
                        <span className="check-partial">{row.free}</span>
                      )}
                    </td>
                    <td>
                      {row.pro === "✓" || row.pro.startsWith("✓") ? (
                        <span className="check-yes">{row.pro}</span>
                      ) : (
                        <span className="check-partial">{row.pro}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="pricing-faq">
        <Container>
          <h2 className="pricing-faq-title">Frequently Asked Questions</h2>
          <Row>
            <Col lg="8" className="mx-auto">
              <Accordion
                open={openAccordion}
                toggle={toggleAccordion}
                className="faq-accordion"
              >
                {FAQ_ITEMS.map((item) => (
                  <AccordionItem key={item.id}>
                    <AccordionHeader targetId={item.id}>{item.q}</AccordionHeader>
                    <AccordionBody accordionId={item.id}>{item.a}</AccordionBody>
                  </AccordionItem>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="pricing-cta">
        <Container>
          <h2>See what today's charts looked like before</h2>
          <p>
            Every signal is free. Upgrade when you want unlimited searches
            and alerts that come to you.
          </p>
          <div className="cta-buttons">
            {isPro ? (
              <Link to="/account">
                <Button
                  color="light"
                  size="lg"
                  style={{ fontWeight: 600, color: "var(--primary)" }}
                >
                  Manage Account
                </Button>
              </Link>
            ) : (
              <Button
                color="light"
                size="lg"
                style={{ fontWeight: 600, color: "var(--primary)" }}
                onClick={handleStartTrial}
                disabled={checkoutLoading}
              >
                {checkoutLoading ? <Spinner size="sm" /> : "Start Free Trial"}
              </Button>
            )}
            <Link to="/about">
              <Button
                outline
                color="light"
                size="lg"
                style={{ fontWeight: 600, color: "white", borderColor: "white" }}
              >
                How It Works
              </Button>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
